import { Facebook, Instagram } from 'lucide-react';
import { Logo } from './Logo';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { OPEN_COOKIE_BANNER_EVENT } from '@/utils/cookiePreferences';

export function Footer() {
  const { t } = useTranslation();

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new Event(OPEN_COOKIE_BANNER_EVENT));
  };

  const footerLinks = {
    produkt: [
      { name: t('nav.features'), href: '/#functions' },
      { name: t('nav.pricing'), href: '/pricing' },
    ],

    prawne: [
      { name: t('legal.terms'), href: '/terms' },
      { name: t('legal.privacy'), href: '/privacy-policy' },
      { name: t('legal.cookies'), href: '/cookie-policy' },
      { name: t('legal.gdpr'), href: '/gdpr' },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/trenioapp/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/trenioapp/', label: 'Instagram' },
  ];

  return (
    <footer className="relative border-t border-border bg-background px-4 pt-16 pb-8 text-[15px] text-foreground md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1 lg:col-span-2">
            <Link to="/" aria-label="TrenioApp" className="mb-4 inline-flex">
              <Logo />
            </Link>
            <p className="text-muted-foreground mb-6 max-w-sm leading-6">
              {t('footer.tagline')}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="mb-4 text-sm text-muted-foreground">{t('footer.product')}</h3>
            <ul className="space-y-2.5">
              {footerLinks.produkt.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('#') ? (
                    <a
                      href={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>



          <div>
            <h3 className="mb-4 text-sm text-muted-foreground">{t('footer.legal')}</h3>
            <ul className="space-y-2.5">
              {footerLinks.prawne.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('#') || link.href === '#' ? (
                    <a
                      href={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={handleOpenCookieSettings}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('cookies.managePreferences')}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-border pt-8 mb-8">
          <div className="max-w-md mx-auto lg:mx-0">
            <h3 className="mb-3 text-sm text-muted-foreground">
              {t('footer.newsletterTitle')}
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                className="h-10 flex-1 rounded-lg surface px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => toast.success(t('footer.newsletterSuccess'))}
                className="h-10 w-full rounded-lg bg-foreground px-4 text-sm font-medium text-background hover:opacity-90 transition-opacity sm:w-auto"
              >
                {t('footer.newsletterButton')}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-mono text-xs text-muted-foreground tabular">
            © {new Date().getFullYear()} TrenioApp. {t('footer.allRightsReserved')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">
              {t('legal.terms')}
            </Link>
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
              {t('legal.privacy')}
            </Link>
            <Link to="/cookie-policy" className="hover:text-foreground transition-colors">
              {t('legal.cookies')}
            </Link>
            <button
              type="button"
              onClick={handleOpenCookieSettings}
              className="hover:text-foreground transition-colors"
            >
              {t('cookies.managePreferences')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}